'use client';

import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useClientLogger } from '@/app/utils/clientLogger';

type OrientationLockType = 
  | 'any'
  | 'natural'
  | 'landscape'
  | 'portrait'
  | 'portrait-primary'
  | 'portrait-secondary'
  | 'landscape-primary'
  | 'landscape-secondary';

interface OrientationLockContextType {
  isSupported: boolean;
  lockOrientation: (orientation: OrientationLockType) => Promise<void>;
  unlockOrientation: () => void;
}

const OrientationLockContext = createContext<OrientationLockContextType | undefined>(undefined);

interface OrientationLockProviderProps {
  children: ReactNode;
  orientation?: OrientationLockType;
}

export function OrientationLockProvider({
  children,
  orientation = 'any'
}: OrientationLockProviderProps) {
  const logger = useClientLogger();
  const isSupported = typeof window !== 'undefined' &&
    'screen' in window && 
    'orientation' in window.screen && 
    'lock' in window.screen.orientation;

  const lockOrientation = async (newOrientation: OrientationLockType): Promise<void> => {
    if (!isSupported) {
      logger.log('warn', 'Screen Orientation API is not supported');
      return;
    }

    try {
      await (window.screen.orientation as ScreenOrientation & { lock: (orientation: OrientationLockType) => Promise<void> }).lock(newOrientation);
    } catch (error) {
      logger.log('warn', 'Failed to lock orientation', { detail: error instanceof Error ? error.message : String(error) });
    }
  };

  const unlockOrientation = (): void => {
    if (!isSupported) {
      logger.log('warn', 'Screen Orientation API is not supported');
      return;
    }

    try {
      window.screen.orientation.unlock();
    } catch (error) {
      logger.log('warn', 'Failed to unlock orientation', { detail: error instanceof Error ? error.message : String(error) });
    }
  };

  useEffect(() => {
    if (orientation !== 'any') {
      lockOrientation(orientation);
    }

    return () => {
      if (orientation !== 'any') {
        unlockOrientation();
      }
    };
  }, [orientation]);

  const contextValue: OrientationLockContextType = {
    isSupported,
    lockOrientation,
    unlockOrientation
  };

  return (
    <OrientationLockContext.Provider value={contextValue}>
      {children}
    </OrientationLockContext.Provider>
  );
}

export function useOrientationLock(): OrientationLockContextType {
  const context = useContext(OrientationLockContext);
  if (context === undefined) {
    throw new Error('useOrientationLock must be used within an OrientationLockProvider');
  }
  return context;
}
